<template lang="pug">
base-section.theses-section(:space='0')
  base-page-content
    .theses-shell
      section.theses-intro
        .theses-intro-copy
          p.section-kicker {{ copy.intro.kicker }}
          h2.section-heading {{ copy.intro.heading }}
          p.section-body {{ copy.intro.body }}
        .theses-focus
          h3 {{ copy.focus.heading }}
          ul.focus-list
            li(v-for='item in copy.focus.items', :key='item') {{ item }}
        p.section-note {{ copy.intro.note }}

      section.theses-topics
        .section-header
          p.section-kicker {{ copy.topics.kicker }}
          h2.section-heading {{ copy.topics.heading }}
        person-thesis-tab(:theses='theses')

      section.theses-guidance
        .guidance-card(v-for='card in copy.guidance', :key='card.heading')
          p.section-kicker {{ card.kicker }}
          h2.section-heading {{ card.heading }}
          p.section-body(v-if='card.lead') {{ card.lead }}
          .authority-links(v-if='card.authorityLinks.length')
            a.authority-link(
              v-for='link in card.authorityLinks',
              :key='link.href',
              :href='link.href',
              target='_blank',
              rel='noopener noreferrer'
            )
              v-icon(size='20') {{ link.icon }}
              span {{ link.label }}
          ol.process-flow(v-if='card.steps.length')
            li.process-step(v-for='(item, index) in card.steps', :key='item.heading')
              .process-marker
                span {{ index + 1 }}
              .process-content
                h3.process-heading {{ item.heading }}
                p.process-body {{ item.body }}
          p.section-body(v-if='card.body') {{ card.body }}
          .template-block(v-for='block in card.templateBlocks', :key='block.heading')
            h3.template-heading {{ block.heading }}
            p.section-body {{ block.body }}
            ul.template-links
              li(v-for='link in block.links', :key='link.href')
                a(:href='link.href', target='_blank', rel='noopener noreferrer') {{ link.label }}
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Thesis } from '~/models/thesis'

defineOptions({
  name: 'ThesesSection',
})

defineProps({
  theses: {
    type: Array as () => Array<Thesis>,
    required: true,
  },
})

const { locale } = useI18n()

const THESIS_TEMPLATE_LINKS = {
  proposal:
    'https://gitlab.uni-koblenz.de/ag-riehle/research-proposal-template/-/archive/master/research-proposal-template-master.zip',
  word: 'https://gitlab.uni-koblenz.de/ag-riehle/thesis-template/-/archive/master/thesis-template-master.zip',
  latex:
    'https://gitlab.uni-koblenz.de/ag-riehle/thesis-template-latex/-/archive/master/thesis-template-latex-master.zip',
  presentation:
    'https://gitlab.uni-koblenz.de/ag-riehle/presentation-template/-/archive/main/presentation-template-main.zip',
} as const

const thesisCopy = {
  en: {
    intro: {
      kicker: 'Thesis Supervision',
      heading: 'Topics for applied research work',
      body: 'Final theses in the research group are usually not pure literature theses. They typically combine conceptual work with prototyping, data collection, or empirical analysis.',
      note: 'Please refrain from inquiries that cannot be assigned to one of the listed areas.',
    },
    focus: {
      heading: 'Focus areas',
      items: [
        'Sensors and Internet-of-Things (LoRa, etc.)',
        'Cloud architectures, infrastructure, virtualization, and orchestration',
        'Data management and data analytics',
      ],
    },
    topics: {
      kicker: 'Topics',
      heading: 'Current thesis opportunities',
    },
    guidance: [
      {
        kicker: 'Application',
        heading: 'How to apply for a topic',
        lead: 'Before contacting the research group, read the official information provided by the University Examination Office. Access to the information PDF requires University credentials.',
        authorityLinks: [
          {
            href: 'https://www.uni-koblenz.de/de/hochschulpruefungsamt/downloads/grundsaetzliches-aa_ba-ma_fb4-rpo.pdf/',
            label: 'Information PDF from the Examination Office',
            icon: 'mdi-file-document-outline',
          },
          {
            href: 'https://www.uni-koblenz.de/de/hochschulpruefungsamt',
            label: 'Website of the Hochschulprüfungsamt',
            icon: 'mdi-open-in-new',
          },
        ],
        steps: [
          {
            heading: 'Clarify the official process',
            body: 'Regulations differ between fields of study. The research group cannot provide binding process information; contact the Hochschulprüfungsamt for questions about registration, deadlines, forms, and examination rules.',
          },
          {
            heading: 'Contact the topic tutor',
            body: 'Send an e-mail to the tutor responsible for the topic. Briefly explain your motivation, attach an excerpt of your academic record, and indicate the period in which you would like to write the thesis.',
          },
          {
            heading: 'Prepare and approve the Exposé',
            body: 'After the topic discussion, prepare the research proposal using our template. The Exposé must be reviewed and approved by the supervisor before the thesis is registered with the Prüfungsamt.',
          },
          {
            heading: 'Register the thesis',
            body: 'Once the Exposé has been approved, register the thesis with the Prüfungsamt according to the rules and forms that apply to your degree program.',
          },
          {
            heading: 'Work on the thesis in the Oberseminar',
            body: 'During the thesis period, participation in the Oberseminar course is required. The Oberseminar includes a starter presentation near the beginning and a defense talk at the end.',
          },
        ],
        body: '',
        templateBlocks: [],
      },
      {
        kicker: 'Templates',
        heading: 'Documents and working materials',
        lead: '',
        authorityLinks: [],
        steps: [],
        body: '',
        templateBlocks: [
          {
            heading: 'Research proposal (Expose)',
            body: 'Before the thesis starts, a research proposal based on our template must be submitted to the tutor for approval. It should cover motivation, objectives, and methodological approach in 1-2 pages and already reference core literature.',
            links: [
              {
                href: THESIS_TEMPLATE_LINKS.proposal,
                label: 'Research proposal template for theses',
              },
            ],
          },
          {
            heading: 'Writing and defense',
            body: 'Processing time is defined by the relevant examination regulations and is usually six months. For the thesis document and defense, please use the following working group templates.',
            links: [
              {
                href: THESIS_TEMPLATE_LINKS.word,
                label: 'Word template for theses',
              },
              {
                href: THESIS_TEMPLATE_LINKS.latex,
                label: 'LaTeX template for theses',
              },
              {
                href: THESIS_TEMPLATE_LINKS.presentation,
                label: 'PowerPoint template for talks and defenses',
              },
            ],
          },
        ],
      },
    ],
  },
  de: {
    intro: {
      kicker: 'Abschlussarbeiten',
      heading:
        'Strukturierte Themen für anwendungsorientierte Forschungsarbeiten',
      body: 'Abschlussarbeiten in der Arbeitsgruppe sind in der Regel keine reinen Literaturarbeiten. Sie verbinden konzeptionelle Arbeit meist mit Prototyping, Datenerhebung oder Analyse.',
      note: 'Bitte sehen Sie von Anfragen ab, die sich keinem dieser Bereiche zuordnen lassen.',
    },
    focus: {
      heading: 'Themenfelder',
      items: [
        'Sensoren und Internet-of-Things (LoRa, etc.)',
        'Cloud-Architekturen, Infrastrukturen, Virtualisierung und Orchestrierung',
        'Datenmanagement und Data Analytics',
      ],
    },
    topics: {
      kicker: 'Themen',
      heading: 'Aktuelle Themen für Abschlussarbeiten',
    },
    guidance: [
      {
        kicker: 'Bewerbung',
        heading: 'Vorgehen bei der Bewerbung',
        lead: 'Lesen Sie vor der Kontaktaufnahme mit der Arbeitsgruppe die offiziellen Informationen des Hochschulprüfungsamts. Für das Informations-PDF ist eine Anmeldung mit den Universitätszugangsdaten erforderlich.',
        authorityLinks: [
          {
            href: 'https://www.uni-koblenz.de/de/hochschulpruefungsamt/downloads/grundsaetzliches-aa_ba-ma_fb4-rpo.pdf/',
            label: 'Informations-PDF des Hochschulprüfungsamts',
            icon: 'mdi-file-document-outline',
          },
          {
            href: 'https://www.uni-koblenz.de/de/hochschulpruefungsamt',
            label: 'Website des Hochschulprüfungsamts',
            icon: 'mdi-open-in-new',
          },
        ],
        steps: [
          {
            heading: 'Offiziellen Prozess klären',
            body: 'Die Regelungen unterscheiden sich je nach Studiengang. Die Arbeitsgruppe kann keine verbindlichen Auskünfte zum Verfahren geben; wenden Sie sich bei Fragen zu Anmeldung, Fristen, Formularen und Prüfungsordnung an das Hochschulprüfungsamt.',
          },
          {
            heading: 'Themenbetreuung kontaktieren',
            body: 'Schreiben Sie der für das Thema zuständigen Betreuung eine E-Mail. Erläutern Sie kurz Ihre Motivation, fügen Sie einen Auszug Ihrer bisherigen Studienleistungen bei und nennen Sie den geplanten Bearbeitungszeitraum.',
          },
          {
            heading: 'Exposé erstellen und abstimmen',
            body: 'Nach der Themenbesprechung erstellen Sie das Exposé anhand unserer Vorlage. Das Exposé muss vor der Anmeldung der Abschlussarbeit beim Prüfungsamt durch die Betreuung freigegeben werden.',
          },
          {
            heading: 'Abschlussarbeit anmelden',
            body: 'Nach Freigabe des Exposés melden Sie die Abschlussarbeit beim Prüfungsamt gemäß den für Ihren Studiengang geltenden Regeln und Formularen an.',
          },
          {
            heading: 'Bearbeitung im Oberseminar begleiten',
            body: 'Während der Bearbeitungszeit ist die Teilnahme am Oberseminar erforderlich. Im Oberseminar finden eine Starter-Präsentation zu Beginn und die Verteidigung am Ende der Arbeit statt.',
          },
        ],
        body: '',
        templateBlocks: [],
      },
      {
        kicker: 'Vorlagen',
        heading: 'Dokumente und Arbeitsmaterialien',
        lead: '',
        authorityLinks: [],
        steps: [],
        body: '',
        templateBlocks: [
          {
            heading: 'Expose',
            body: 'Vor Beginn jeder Abschlussarbeit ist ein Expose anhand unserer Vorlage zu erstellen und an die Betreuung zur Abstimmung zu senden. Es behandelt Motivation, Zielsetzung und methodisches Vorgehen auf 1-2 Seiten und sollte bereits zentrale Literatur enthalten.',
            links: [
              {
                href: THESIS_TEMPLATE_LINKS.proposal,
                label: 'Expose-Vorlage für Abschlussarbeiten',
              },
            ],
          },
          {
            heading: 'Verfassen und Verteidigung',
            body: 'Die Bearbeitungszeit richtet sich nach Ihrer Prüfungsordnung und beträgt in der Regel sechs Monate. Für Ausarbeitung und Kolloquium verwenden Sie bitte die folgenden Vorlagen der Arbeitsgruppe.',
            links: [
              {
                href: THESIS_TEMPLATE_LINKS.word,
                label: 'Word-Vorlage für Abschlussarbeiten',
              },
              {
                href: THESIS_TEMPLATE_LINKS.latex,
                label: 'LaTeX-Vorlage für Abschlussarbeiten',
              },
              {
                href: THESIS_TEMPLATE_LINKS.presentation,
                label: 'PowerPoint-Vorlage für Vorträge und Kolloquien',
              },
            ],
          },
        ],
      },
    ],
  },
} as const

const copy = computed(() => thesisCopy[locale.value === 'de' ? 'de' : 'en'])
</script>

<style scoped>
.theses-section {
  padding: 0 0 5rem;
  background:
    linear-gradient(
      180deg,
      color-mix(in srgb, var(--surface-muted) 90%, var(--surface-page)) 0%,
      var(--surface-page) 18rem
    ),
    radial-gradient(
      circle at top right,
      rgba(0, 192, 216, 0.08),
      transparent 24rem
    );
}

.theses-shell {
  display: grid;
  gap: 2rem;
}

.theses-intro,
.guidance-card {
  border: 1px solid var(--border-default);
  background: var(--surface-card);
}

.theses-intro {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(16rem, 1fr);
  gap: 1.5rem;
  padding: 1.5rem;
}

.theses-intro-copy {
  min-width: 0;
}

.theses-focus {
  padding: 1.1rem 1.2rem;
  background: var(--surface-muted);
  border-left: 3px solid var(--brand-primary);
}

.theses-focus h3,
.template-heading {
  margin: 0;
  color: var(--color-text-strong);
  font-size: 1.05rem;
  font-family: var(--font-sans);
}

.focus-list,
.template-links {
  margin: 0.9rem 0 0;
  padding-left: 1.2rem;
  color: var(--color-text);
  line-height: 1.75;
}

.section-header {
  margin-bottom: 1rem;
}

.section-kicker {
  margin: 0 0 0.7rem;
  color: var(--color-accent-soft);
  text-transform: uppercase;
  letter-spacing: 0.16em;
  font-size: 0.76rem;
  font-weight: 700;
}

.section-heading {
  margin: 0;
  color: var(--color-text-strong);
  font-size: clamp(1.6rem, 2.5vw, 2.2rem);
  line-height: 1;
  font-family: var(--font-sans);
}

.section-body,
.section-note {
  margin: 1rem 0 0;
  color: var(--color-text);
  line-height: 1.7;
  font-family: var(--font-sans);
  text-align: justify;
  hyphens: auto;
}

.section-note {
  grid-column: 1 / -1;
  margin-top: 0;
  padding-top: 0.4rem;
  color: var(--color-text-muted);
}

.theses-guidance {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;
}

.guidance-card {
  padding: 1.4rem 1.5rem;
}

.authority-links {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1.1rem;
}

.authority-link {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
  padding: 0.85rem 0.95rem;
  color: var(--brand-ink);
  font-weight: 700;
  line-height: 1.35;
  text-decoration: none;
  background: color-mix(in srgb, var(--brand-primary) 8%, var(--surface-page));
  border: 1px solid color-mix(in srgb, var(--brand-primary) 30%, transparent);
}

.authority-link:hover {
  color: var(--brand-primary);
  border-color: var(--brand-primary);
}

.process-flow {
  display: grid;
  gap: 0.85rem;
  margin: 1.3rem 0 0;
  padding: 0;
  list-style: none;
  counter-reset: process-step;
}

.process-step {
  position: relative;
  display: grid;
  grid-template-columns: 2.5rem minmax(0, 1fr);
  gap: 0.85rem;
}

.process-step:not(:last-child)::before {
  position: absolute;
  top: 2.75rem;
  bottom: -0.85rem;
  left: 1.2rem;
  width: 2px;
  content: '';
  background: var(--border-default);
}

.process-marker {
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  width: 2.5rem;
  height: 2.5rem;
  color: var(--surface-page);
  font-weight: 800;
  background: var(--brand-primary);
  border: 3px solid var(--surface-card);
}

.process-content {
  min-width: 0;
  padding: 0.75rem 0.9rem;
  background: var(--surface-muted);
  border-left: 3px solid var(--border-default);
}

.process-heading {
  margin: 0;
  color: var(--color-text-strong);
  font-family: var(--font-sans);
  font-size: 1rem;
  line-height: 1.25;
}

.process-body {
  margin: 0.4rem 0 0;
  color: var(--color-text);
  font-family: var(--font-sans);
  line-height: 1.65;
}

.template-block + .template-block {
  margin-top: 1.4rem;
  padding-top: 1.4rem;
  border-top: 1px solid var(--border-subtle);
}

.template-links a {
  color: var(--brand-primary);
  text-decoration: none;
  font-weight: 700;
}

.template-links a:hover {
  color: var(--brand-ink);
  text-decoration: underline;
}

@media (max-width: 960px) {
  .theses-intro,
  .theses-guidance {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .theses-section {
    padding-bottom: 4rem;
  }

  .theses-intro,
  .guidance-card {
    padding: 1rem;
  }

  .authority-links {
    grid-template-columns: 1fr;
  }

  .process-step {
    grid-template-columns: 2.2rem minmax(0, 1fr);
    gap: 0.7rem;
  }

  .process-step:not(:last-child)::before {
    left: 1.05rem;
  }

  .process-marker {
    width: 2.2rem;
    height: 2.2rem;
  }
}
</style>
